import type { TB2cClaims, TEmail, TSignInEmail } from '@/contexts/user-authorizations/UserPermissions';
import type { AccountInfo } from '@azure/msal-browser';

// Type guards
export const hasSignInEmail = (obj: unknown): obj is TSignInEmail =>
  typeof obj === 'object' &&
  obj !== null &&
  Object.prototype.hasOwnProperty.call(obj, 'signInNames') &&
  typeof (obj as Record<string, unknown>).signInNames === 'object' &&
  (obj as Record<string, unknown>).signInNames !== null &&
  Object.prototype.hasOwnProperty.call((obj as Record<string, unknown>).signInNames, 'emailAddress') &&
  typeof ((obj as Record<string, unknown>).signInNames as Record<string, unknown>).emailAddress === 'string';

export const hasEmail = (obj: unknown): obj is TEmail =>
  typeof obj === 'object' &&
  obj !== null &&
  Object.prototype.hasOwnProperty.call(obj, 'email') &&
  typeof (obj as Record<string, unknown>).email === 'string';

export const hasB2cClaims = (obj: unknown): obj is TB2cClaims => typeof obj === 'object' && obj !== null;

// Account data extractors
export const retrieveAccountHolderName = (account: AccountInfo) => {
  const { idTokenClaims: info } = account;
  let names = {
    firstName: '',
    surName: '',
    fullName: '',
  };
  if (info) {
    const firstName = info['given_name'] as string;
    const surName = info['family_name'] as string;
    names = {
      firstName,
      surName,
      fullName: `${firstName}${surName ? ' ' + surName : ''}`,
    };
  }
  return names;
};

export const retrieveAccountName = (account: AccountInfo) => {
  const { name } = account;
  if (name) {
    return name;
  }
  return retrieveAccountHolderName(account).fullName;
};

export const retrieveEmail = (account: AccountInfo) => {
  const { username, idTokenClaims: info } = account;
  if (username) {
    return username;
  }
  if (hasSignInEmail(info) && info.signInNames.emailAddress) {
    return info.signInNames.emailAddress;
  }
  if (hasEmail(info) && info.email) {
    return info.email;
  }
  if (info?.emails?.length) {
    return info.emails[0];
  }
  return '';
};

export const retrieveB2cId = (account: AccountInfo): string => {
  const { idTokenClaims } = account;
  if (hasB2cClaims(idTokenClaims)) {
    return idTokenClaims.b2cId || idTokenClaims.sub || '';
  }
  return '';
};

export const retrieveEndCustomerId = (account: AccountInfo): string | undefined => {
  const { idTokenClaims } = account;
  if (hasB2cClaims(idTokenClaims)) {
    return idTokenClaims.endCustomerId || undefined;
  }
  return undefined;
};

// Permission logic helper
export const includesNeededScope = (userPermission: string, neededPermission: string) => {
  const [uScope, uRule] = userPermission.split('.');
  const [vScope, vRule] = neededPermission.split('.');

  switch (true) {
    case !!vScope && !vRule:
      return uScope === vScope && !uRule;
    case !!vScope && !!vRule:
      return userPermission === neededPermission || (!!uScope && !uRule && uScope === vScope);
    default:
      return false;
  }
};
